import { useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { Box, Loading } from '@factorialco/deprecated-design-system'
import {
  EmployeesEmployeeSelectorInput,
  TrainingClassDetailIndexPageDocument,
  UpdateSessionInstructorsDocument,
} from 'generated/resources/graphql'

import Sidepanel, { type Option } from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { checkFeatures } from 'modules/core/hooks/useFeatures'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EventBox } from 'modules/trainings/components/Revamp/NewClassWizard/EventDataStep/EventBox'
import {
  SessionData,
  sessionDataFromSessionClassFragment,
} from 'modules/trainings/components/Revamp/NewClassWizard/hooks/useNewClassWizardSteps'
import { formatDueDate } from 'modules/trainings/components/Revamp/TrainingsDetail/Classes/EditClass/ClassEvents'
import { useCreateSessionRelatorioUnicoField } from 'modules/trainings/hooks/SessionRelatorioUnicoFields/useCreateSessionRelatorioUnicoField'
import { useUpdateSessionRelatorioUnicoField } from 'modules/trainings/hooks/SessionRelatorioUnicoFields/useUpdateSessionRelatorioUnicoField'
import { useUpdateSession } from 'modules/trainings/hooks/Sessions/useUpdateSession'
import { useIsRelatorioEnabled } from 'modules/trainings/hooks/Settings/settingUtils'

const EditSessionSidepanel = () => {
  const { id, classId } = useOutletContext<{
    id: string
    classId: string
  }>()
  const { eventId } = useParams()
  const { company } = useCurrentSession()
  const { data, loading } = useQuery(TrainingClassDetailIndexPageDocument, {
    variables: {
      companyId: company.id,
      trainingClassId: Number(classId),
      withFF: true,
    },
  })
  const [updateSessionInstructors] = useMutation(UpdateSessionInstructorsDocument, {
    extractorFn: (data) => data.trainingsMutations.bulkUpdateTrainingsSessionInstructor,
  })
  const actualClass = data?.trainings.trainingClass
  const currentSession =
    actualClass?.sessions &&
    actualClass?.sessions?.find((session) => String(session.id) === String(eventId))
  const { has_dev_trainings: devTrainings } = checkFeatures(
    ['dev_trainings'],
    data?.features?.featuresConnection.nodes
  )
  const sessions = actualClass?.sessions ?? []
  const relatorioEnabled = useIsRelatorioEnabled(company.id)
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const back = () =>
    navigate(
      navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.fullPath({
        id: Number(id),
        classId: Number(classId),
      })
    )
  const { update: updateRU } = useUpdateSessionRelatorioUnicoField()
  const { create: createRU } = useCreateSessionRelatorioUnicoField()

  const sessionDataToEdit = currentSession && sessionDataFromSessionClassFragment(currentSession)

  const { update } = useUpdateSession()
  const updateSession = async (session: SessionData) => {
    track(trackingEvents.training.trainingSession.editSessionSave)
    const dueDate = formatDueDate(session.dueDate)

    const response = await update({
      ...session,
      id: Number(session.id),
      subsidized: session.subsidized ?? false,
      dueDate: dueDate ?? '',
      recurrent: session.recurrent,
    })
    const handled = handleResult(response, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('trainings.trainings_revamp.classes_sidepanel.events.error_update'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.trainings_revamp.classes_sidepanel.events.success_update'),
      })

      onHandleSubmitRelatorio(
        Number(session.id),
        session.sessionRelatorioUnicoField?.trainingSchedule
      )

      onHandleSubmitSessionInstructors(
        session.sessionInstructors.employeeSelection,
        String(session.id),
        company.id
      )

      back()
    }
  }
  const optionsSidepanel: Option[] = [
    {
      danger: true,
      icon: 'delete',
      title: i18n.t('trainings.trainings_revamp.event_sidepanel.dropdown.delete'),
      to: navigation.trainings.list.details_revamp.view.trainingClasses.tab.event.view.deleteEvent.fullPath(
        {
          id: Number(id),
          classId: Number(classId),
          eventId: Number(eventId),
        }
      ),
    },
  ]
  const onHandleSubmitRelatorio = async (eventId: number, trainingSchedule?: string) => {
    if (relatorioEnabled) {
      const sessionRelatorio = currentSession?.sessionRelatorioUnicoField

      if (sessionRelatorio) {
        const response = await updateRU({
          ...sessionRelatorio,
          id: sessionRelatorio.id,
          trainingSchedule: trainingSchedule,
        })
        const handled = handleResult(response, () => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('trainings.training_relatorio_fields.fields_updated_error'),
          })
        })
        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('trainings.training_relatorio_fields.fields_updated'),
          })
        }
      } else if (trainingSchedule) {
        const response = await createRU({
          sessionId: eventId,
          trainingSchedule: trainingSchedule,
        })
        const handled = handleResult(response, () => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('trainings.training_relatorio_fields.fields_created_error'),
          })
        })
        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('trainings.training_relatorio_fields.fields_created'),
          })
        }
      }
    }
  }

  const onHandleSubmitSessionInstructors = async (
    employeeSelection: EmployeesEmployeeSelectorInput,
    sessionId: string,
    companyId: number
  ) => {
    const response = await updateSessionInstructors({
      variables: {
        employeeSelection,
        sessionId,
        companyId,
      },
    })
    const handled = handleResult(response, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'trainings.trainings_revamp.classes_sidepanel.events.session_instructors.update_error'
        ),
      })
    })
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t(
          'trainings.trainings_revamp.classes_sidepanel.events.session_instructors.update_success'
        ),
      })
    }
  }

  if (!currentSession) {
    back()
    return
  }
  if (loading) return <Loading />
  return (
    <Sidepanel
      width="wideXL"
      open
      title={currentSession.name}
      onClose={back}
      options={sessions.length > 1 ? optionsSidepanel : undefined}
    >
      <Box gap="s24">
        <EventBox
          trainingId={Number(actualClass?.training?.id)}
          trainingClassId={Number(actualClass.id)}
          initialData={sessionDataToEdit}
          onClose={back}
          onSave={(session: SessionData) => updateSession(session)}
          groupStart={actualClass?.startDate ?? ''}
          groupEnd={actualClass?.endDate ?? ''}
          featureFlag={!!devTrainings}
        />
      </Box>
    </Sidepanel>
  )
}

export default EditSessionSidepanel
